/**
 * Admin API Service for Grand Pittu Admin Panel
 * Extends the existing frontend API service patterns with admin-specific functionality
 */

import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse, AxiosInstance } from 'axios';
import { API_CONFIG } from '../constants/config';
import Cookies from 'js-cookie';
import authService from './authService';
import type {
  PaginatedResponse,
  ReserveTable,
  BookingRequestDto,
  RejectRequestDto,
  AcceptRessponseDto
} from '../types/reservations';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class AdminApiService {
  private api: AxiosInstance;
  private cache = new Map<string, CacheEntry<unknown>>();
  private readonly cacheTimeout = 5 * 60 * 1000; // 5 minutes
  private readonly baseURL: string;

  constructor() {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend cookie config)
    const isDev = import.meta.env.DEV;

    if (isDev) {
      // Use Vite proxy - requests go to localhost/api/v1 and are proxied to backend
      // This ensures cookies are same-origin and persist across page refreshes
      this.baseURL = '/api/v1';
    } else {
      const baseUrl = import.meta.env.VITE_API_URL || 'https://grandpittu-backend.onrender.com';
      this.baseURL = `${baseUrl}${API_CONFIG.SLUG}`;
    }

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000, // 30 seconds for file uploads
      // Don't set default Content-Type - let Axios detect based on data
      // CRITICAL: Include credentials (cookies) with every request for HTTP-only cookie auth
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Ensure withCredentials is always set for HTTP-only cookie auth
        config.withCredentials = true;

        // Set Content-Type based on data type
        if (config.data instanceof FormData) {
          // Let browser set Content-Type with boundary for FormData
          delete config.headers['Content-Type'];
        } else if (!config.headers['Content-Type']) {
          // Default to JSON for other requests
          config.headers['Content-Type'] = 'application/json';
        }

        // Add timestamp to prevent caching for write operations
        if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
          config.params = { ...config.params, _t: Date.now() };
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't handle auth errors for auth endpoints
        if (originalRequest?.url?.includes('/auth/')) {
          return Promise.reject(error);
        }

        // Handle token expiration - let auth service handle it
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Let auth service handle token refresh
            // Backend refreshes token via HTTP-only cookies
            await authService.refreshAccessToken();
            // Retry request - cookies will be sent automatically
            return this.api.request(originalRequest);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            authService.handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }

        // Don't show generic toast here - let the calling code use errorHandler
        // This allows better control over error messages
        return Promise.reject(error);
      }
    );
  }

  private setAuthHeader(token: string) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  private removeAuthHeader() {
    delete this.api.defaults.headers.common['Authorization'];
  }

  private getCacheKey(endpoint: string, params?: Record<string, unknown>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${endpoint}${paramString}`;
  }

  private isValidCache<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < this.cacheTimeout;
  }

  private setCache<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T>;
    if (entry && this.isValidCache(entry)) {
      return entry.data;
    }
    this.cache.delete(key);
    return null;
  }

  // Generic fetch method with caching
  async fetch<T>(
    endpoint: string,
    options: AxiosRequestConfig = {},
    useCache = true
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, options.params);

    // Return cached data for GET requests
    if (useCache && (!options.method || options.method.toUpperCase() === 'GET')) {
      const cached = this.getCache<T>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      const response: AxiosResponse<T> = await this.api(endpoint, options);

      // Cache successful GET responses
      if (useCache && (!options.method || options.method.toUpperCase() === 'GET')) {
        this.setCache(cacheKey, response.data);
      }

      // Clear related cache on write operations
      if (options.method && ['post', 'put', 'patch', 'delete'].includes(options.method.toLowerCase())) {
        this.clearRelatedCache(endpoint);
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  private clearRelatedCache(endpoint: string): void {
    const baseEndpoint = endpoint.split('/')[0];
    const keysToDelete = Array.from(this.cache.keys()).filter(key =>
      key.includes(baseEndpoint) || key.includes('dashboard') || key.includes('stats')
    );
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Resend forgot password OTP
   */
  async resendForgotPasswordOTP(email: string): Promise<string> {
    try {
      const response = await this.api.post<string>('/auth/resend-forgotPassword/otp', { email });
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to resend OTP';
      throw new Error(errorMessage);
    }
  }

  // Special method for uploading FormData without JSON transformation
  async uploadFormData(
    endpoint: string,
    formData: FormData,
    method: 'POST' | 'PUT' = 'POST'
  ): Promise<any> {
    try {

      // Use the api instance - interceptor will handle FormData properly
      const response = await this.api.request({
        url: endpoint,
        method: method,
        data: formData,
        params: { _t: Date.now() },
      });

      // Clear related cache after upload
      this.clearRelatedCache(endpoint);

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Helper method to extract table ID from reservation data
  private getTableIdFromReservation(reservation: ReserveTable | any): number {
    // First try to get ID from restaurantTable object
    if (reservation?.restaurantTable?.id) {
      return reservation.restaurantTable.id;
    }

    // Fallback to tableNo if available (this is the table number, not ID)
    if (reservation?.tableNo) {
      return reservation.tableNo;
    }

    // Default fallback
    return 1;
  }

  // Public method to update reservation with proper table ID resolution
  async updateReservationWithReservation(
    id: string,
    data: { status: string; message?: string },
    reservation?: ReserveTable | any
  ): Promise<AcceptRessponseDto> {
    const tableId = reservation ? this.getTableIdFromReservation(reservation) : 1;
    return this.updateReservation(id, { ...data, tableId });
  }

  // Public method to delete reservation with proper table ID resolution
  async deleteReservationWithReservation(
    id: string,
    reservation?: ReserveTable | any
  ): Promise<AcceptRessponseDto> {
    const tableId = reservation ? this.getTableIdFromReservation(reservation) : 1;
    return this.deleteReservation(id, tableId);
  }

  // Authentication methods
  async login(email: string, password: string): Promise<{ user: any; token: string; refreshToken: string }> {
    const response = await this.fetch<{ user: any; token: string; refreshToken: string; expiresIn: number }>(
      '/auth/login',
      {
        method: 'POST',
        data: { email, password },
      },
      false
    );

    // Store tokens
    Cookies.set('admin_token', response.token, { expires: 7, secure: true, sameSite: 'strict' });
    Cookies.set('admin_refresh_token', response.refreshToken, { expires: 30, secure: true, sameSite: 'strict' });

    this.setAuthHeader(response.token);

    return response;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await this.fetch<{ message: string }>(
        `/auth/forgot-password/${email}`,
        {
          method: 'POST',
        },
        false
      );
      return response;
    } catch (error: any) {
      // Extract error message from backend response
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send password reset email. Please try again.';

      // Handle specific error cases
      if (error?.response?.status === 404) {
        throw new Error('Email address not found. Please check and try again.');
      }

      throw new Error(errorMessage);
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await this.fetch<{ message: string }>(
        `/auth/reset-password?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          data: { newPassword },
        },
        false
      );
      return response;
    } catch (error: any) {
      // Extract error message from backend response
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to reset password. Please try again.';

      // Handle specific error cases
      if (error?.response?.status === 400) {
        throw new Error('Invalid or expired reset token. Please request a new password reset link.');
      }

      if (error?.response?.status === 404) {
        throw new Error('Invalid reset token. Please request a new password reset link.');
      }

      throw new Error(errorMessage);
    }
  }

  async refreshToken(refreshToken: string): Promise<{ token: string; expiresIn: number }> {
    const response = await this.fetch<{ token: string; expiresIn: number }>(
      '/auth/refresh',
      {
        method: 'POST',
        data: { refreshToken },
      },
      false
    );

    Cookies.set('admin_token', response.token, { expires: 7, secure: true, sameSite: 'strict' });
    this.setAuthHeader(response.token);

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.fetch('/auth/logout', { method: 'POST' }, false);
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      Cookies.remove('admin_token');
      Cookies.remove('admin_refresh_token');
      this.removeAuthHeader();
      this.cache.clear();
    }
  }

  // Note: getProfile removed - now handled by authService.getProfile() which decodes JWT tokens
  // instead of making API calls

  // Content Management methods (extending existing patterns)
  // ==================== CMS - Global Settings ====================
  async getGlobal(): Promise<any> {
    return this.fetch('/global');
  }

  async getGlobalSettings(): Promise<any> {
    return this.fetch('/global');
  }

  async updateGlobal(data: any): Promise<any> {
    return this.fetch('/global', {
      method: 'PUT',
      data,
    }, false);
  }

  async updateGlobalSettings(data: any): Promise<any> {
    return this.fetch('/global', {
      method: 'PUT',
      data,
    }, false);
  }

  // ==================== CMS - Page Content ====================
  async getAllPageContents(): Promise<any[]> {
    return this.fetch('/page-contents');
  }

  async getPageContent(pageNameOrId?: string | number, sectionName?: string): Promise<any[] | any> {
    // If first param is a number/numeric string, treat as ID
    if (typeof pageNameOrId === 'number' || (typeof pageNameOrId === 'string' && /^\d+$/.test(pageNameOrId))) {
      return this.fetch(`/page-contents/${pageNameOrId}`);
    }

    // Otherwise, treat as pageName filter
    let endpoint = '/page-contents';
    const params: Record<string, string> = {};

    if (pageNameOrId) {
      params['pageName'] = pageNameOrId;
    }
    if (sectionName) {
      params['sectionName'] = sectionName;
    }

    return this.fetch(endpoint, { params });
  }

  async getPageContentsByPage(pageName: string): Promise<any[]> {
    return this.fetch(`/page-contents?pageName=${pageName}`);
  }

  async createPageContent(data: any): Promise<any> {
    return this.fetch('/page-contents', {
      method: 'POST',
      data,
    }, false);
  }

  async updatePageContent(id: string | number, data: any): Promise<any> {
    return this.fetch(`/page-contents/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deletePageContent(id: string | number): Promise<void> {
    return this.fetch(`/page-contents/${id}`, {
      method: 'DELETE',
    }, false);
  }

  // ==================== CMS - Menu Management ====================
  async getMenuCategories(): Promise<any[]> {
    return this.fetch('/menu/categories');
  }

  async getCategories(): Promise<any[]> {
    return this.fetch('/categories');
  }

  async createCategory(data: any): Promise<any> {
    return this.fetch('/categories', {
      method: 'POST',
      data,
    }, false);
  }

  async createMenuCategory(data: any): Promise<any> {
    return this.fetch('/menu/categories', {
      method: 'POST',
      data,
    }, false);
  }

  async updateCategory(id: string | number, data: any): Promise<any> {
    return this.fetch(`/categories/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async updateMenuCategory(id: number | string, data: any): Promise<any> {
    return this.fetch(`/menu/categories/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteCategory(id: string | number): Promise<void> {
    return this.fetch(`/categories/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async deleteMenuCategory(id: number | string): Promise<void> {
    return this.fetch(`/menu/categories/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async reorderMenuCategories(categoryIds: number[]): Promise<void> {
    return this.fetch('/menu/categories/reorder', {
      method: 'PUT',
      data: { categoryIds },
    }, false);
  }

  async getMenuItems(params?: { categoryId?: number | string; featured?: boolean; active?: boolean }): Promise<any[]> {
    return this.fetch('/menu/items', { params });
  }

  async getMenuItem(id: number | string): Promise<any> {
    return this.fetch(`/menu/items/${id}`);
  }

  async createMenuItem(data: any): Promise<any> {
    return this.fetch('/menu/items', {
      method: 'POST',
      data,
    }, false);
  }

  async updateMenuItem(id: string | number, data: any): Promise<any> {
    return this.fetch(`/menu/items/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteMenuItem(id: string | number): Promise<void> {
    return this.fetch(`/menu/items/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async bulkDeleteMenuItems(ids: number[]): Promise<void> {
    return this.fetch('/menu/items/bulk-delete', {
      method: 'DELETE',
      data: { ids },
    }, false);
  }

  async reorderMenuItems(itemIds: number[]): Promise<void> {
    return this.fetch('/menu/items/reorder', {
      method: 'PUT',
      data: { itemIds },
    }, false);
  }

  // ==================== CMS - Gallery ====================
  async getGalleries(params?: { category?: string; published?: boolean }): Promise<any[]> {
    return this.fetch('/galleries', { params });
  }

  async getGallery(id: number | string): Promise<any> {
    return this.fetch(`/galleries/${id}`);
  }

  async createGallery(data: any): Promise<any> {
    return this.fetch('/galleries', {
      method: 'POST',
      data,
    }, false);
  }

  async updateGallery(id: string | number, data: any): Promise<any> {
    return this.fetch(`/galleries/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteGallery(id: string | number): Promise<void> {
    return this.fetch(`/galleries/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async bulkDeleteGalleries(ids: (number | string)[]): Promise<void> {
    return this.fetch('/galleries/bulk-delete', {
      method: 'POST',
      data: { ids },
    }, false);
  }

  // ==================== CMS - Sliders ====================
  async getSliders(params?: { active?: boolean }): Promise<any[]> {
    return this.fetch('/sliders', { params });
  }

  async getSlider(id: number | string): Promise<any> {
    return this.fetch(`/sliders/${id}`);
  }

  async createSlider(data: any): Promise<any> {
    return this.fetch('/sliders', {
      method: 'POST',
      data,
    }, false);
  }

  async updateSlider(id: string | number, data: any): Promise<any> {
    return this.fetch(`/sliders/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteSlider(id: string | number): Promise<void> {
    return this.fetch(`/sliders/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async reorderSliders(sliderIds: (number | string)[]): Promise<any> {
    return this.fetch('/sliders/reorder', {
      method: 'POST',
      data: { sliderIds },
    }, false);
  }

  // ==================== CMS - Testimonials ====================
  async getTestimonials(params?: { approved?: boolean; featured?: boolean }): Promise<any[]> {
    return this.fetch('/testimonials', { params });
  }

  async getTestimonial(id: number | string): Promise<any> {
    return this.fetch(`/testimonials/${id}`);
  }

  async createTestimonial(data: any): Promise<any> {
    return this.fetch('/testimonials', {
      method: 'POST',
      data,
    }, false);
  }

  async updateTestimonial(id: string | number, data: any): Promise<any> {
    return this.fetch(`/testimonials/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteTestimonial(id: string | number): Promise<void> {
    return this.fetch(`/testimonials/${id}`, {
      method: 'DELETE',
    }, false);
  }

  // ==================== CMS - Reviews ====================
  async getReviews(params?: { status?: 'pending' | 'approved' | 'rejected' }): Promise<any[]> {
    return this.fetch('/reviews', { params });
  }

  async getReview(id: number | string): Promise<any> {
    return this.fetch(`/reviews/${id}`);
  }

  async moderateReview(id: number | string, data: { status: 'approved' | 'rejected'; moderationNotes?: string }): Promise<any> {
    return this.fetch(`/reviews/${id}/moderate`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteReview(id: number | string): Promise<void> {
    return this.fetch(`/reviews/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async bulkModerateReviews(ids: (number | string)[], status: 'approved' | 'rejected'): Promise<any> {
    return this.fetch('/reviews/bulk-moderate', {
      method: 'POST',
      data: { ids, status },
    }, false);
  }

  // Services management
  async getServices(): Promise<any[]> {
    return this.fetch('/services');
  }

  async createService(data: any): Promise<any> {
    return this.fetch('/services', {
      method: 'POST',
      data,
    }, false);
  }

  async updateService(id: string | number, data: any): Promise<any> {
    return this.fetch(`/services/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteService(id: string | number): Promise<void> {
    return this.fetch(`/services/${id}`, {
      method: 'DELETE',
    }, false);
  }

  // Restaurant operations - Integrated with backend API
  async getPendingReservations(page = 0, size = 10): Promise<PaginatedResponse<ReserveTable>> {
    return this.fetch('/reserveTable/pending', {
      params: { page, size }
    });
  }

  async getAcceptedReservations(page = 0, size = 10): Promise<PaginatedResponse<ReserveTable>> {
    return this.fetch('/reserveTable/accepted', {
      params: { page, size }
    });
  }

  async getReservationsByStatus(status: string, page = 0, size = 10): Promise<PaginatedResponse<ReserveTable>> {
    return this.fetch('/reserveTable/other-status', {
      params: { page, size, status }
    });
  }

  async manageReservation(request: RejectRequestDto): Promise<AcceptRessponseDto> {
    return this.fetch('/reserveTable/manage', {
      method: 'POST',
      data: request
    }, false);
  }

  async createReservation(data: BookingRequestDto): Promise<any> {
    return this.fetch('/book', {
      method: 'POST',
      data,
    }, false);
  }

  // Legacy method for backward compatibility
  async getReservations(params?: { status?: string; date?: string; limit?: number }): Promise<PaginatedResponse<ReserveTable>> {
    const { status = 'PENDING', limit = 10 } = params || {};
    const page = 0;
    const size = limit;

    if (status.toUpperCase() === 'PENDING') {
      return this.getPendingReservations(page, size);
    } else if (status.toUpperCase() === 'ACCEPTED') {
      return this.getAcceptedReservations(page, size);
    } else {
      return this.getReservationsByStatus(status.toUpperCase(), page, size);
    }
  }

  async updateReservation(
    id: string,
    data: { status: string; message?: string; tableId?: number }
  ): Promise<AcceptRessponseDto> {
    // Convert to the backend format
    return this.manageReservation({
      bookingId: parseInt(id),
      bookingStatus: data.status as any,
      message: data.message,
      restaurantTableId: data.tableId ? [data.tableId] : [1] // Backend expects array of table IDs
    });
  }

  async deleteReservation(id: string, tableId?: number): Promise<AcceptRessponseDto> {
    return this.manageReservation({
      bookingId: parseInt(id),
      bookingStatus: 'CANCELED' as any,
      restaurantTableId: tableId ? [tableId] : [1] // Backend expects array of table IDs
    });
  }

  async getTables(): Promise<any[]> {
    return this.fetch('/admin/tables');
  }

  async createTable(data: any): Promise<any> {
    return this.fetch('/admin/tables', {
      method: 'POST',
      data,
    }, false);
  }

  async updateTable(id: string, data: any): Promise<any> {
    return this.fetch(`/admin/tables/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteTable(id: string): Promise<void> {
    return this.fetch(`/admin/tables/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async getFloorPlans(): Promise<any[]> {
    return this.fetch('/admin/floor-plans');
  }

  async updateFloorPlan(floor: number, data: any): Promise<any> {
    return this.fetch(`/admin/floor-plans/${floor}`, {
      method: 'PUT',
      data,
    }, false);
  }

  // Analytics - Updated to match backend documentation
  async getDashboardStats(year?: number): Promise<any> {
    const params = year ? { year } : {};
    return this.fetch('/dashboard/stats', { params }, false); // Don't cache real-time stats
  }

  async getDashboardStatsByMonth(year: number, month: number): Promise<any> {
    return this.fetch('/dashboard/stats/month', {
      params: { year, month }
    }, false);
  }

  async getCurrentMonthStats(): Promise<any> {
    return this.fetch('/dashboard/stats/current-month', {}, false);
  }

  async getMonthlyTrends(year?: number): Promise<any> {
    const params = year ? { year } : {};
    return this.fetch('/dashboard/trends/monthly', { params }, false);
  }

  async getDailyTrends(): Promise<any> {
    return this.fetch('/dashboard/trends/daily', {}, false);
  }

  async getDailyTrendsByMonth(year: number, month: number): Promise<any> {
    return this.fetch('/dashboard/trends/daily/month', {
      params: { year, month }
    }, false);
  }

  async getWebsiteStats(dateRange?: string): Promise<any> {
    return this.fetch('/admin/analytics/website', {
      params: dateRange ? { range: dateRange } : {},
    });
  }

  async getReservationStats(dateRange?: string): Promise<any> {
    return this.fetch('/admin/analytics/reservations', {
      params: dateRange ? { range: dateRange } : {},
    });
  }

  // User management
  async getUsers(): Promise<any[]> {
    return this.fetch('/admin/users');
  }

  async createUser(data: any): Promise<any> {
    return this.fetch('/admin/users', {
      method: 'POST',
      data,
    }, false);
  }

  async updateUser(id: string, data: any): Promise<any> {
    return this.fetch(`/admin/users/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteUser(id: string): Promise<void> {
    return this.fetch(`/admin/users/${id}`, {
      method: 'DELETE',
    }, false);
  }

  // ==================== Reservation Time Slots ====================
  async getTimeSlots(params?: { dayOfWeek?: string; active?: boolean }): Promise<any[]> {
    return this.fetch('/time-slots', { params });
  }

  async getTimeSlot(id: number): Promise<any> {
    return this.fetch(`/time-slots/${id}`);
  }

  async createTimeSlot(data: any): Promise<any> {
    return this.fetch('/time-slots', {
      method: 'POST',
      data,
    }, false);
  }

  async updateTimeSlot(id: number, data: any): Promise<any> {
    return this.fetch(`/time-slots/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteTimeSlot(id: number): Promise<void> {
    return this.fetch(`/time-slots/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async bulkDeleteTimeSlots(ids: number[]): Promise<void> {
    return this.fetch('/time-slots/bulk-delete', {
      method: 'DELETE',
      data: { ids },
    }, false);
  }

  async reorderTimeSlots(slotIds: number[]): Promise<void> {
    return this.fetch('/time-slots/reorder', {
      method: 'PUT',
      data: { slotIds },
    }, false);
  }

  // ==================== Features (for About/Rooftop pages) ====================
  async getFeatures(params?: { category?: string; active?: boolean }): Promise<any[]> {
    return this.fetch('/features', { params });
  }

  async getFeature(id: number): Promise<any> {
    return this.fetch(`/features/${id}`);
  }

  async createFeature(data: any): Promise<any> {
    return this.fetch('/features', {
      method: 'POST',
      data,
    }, false);
  }

  async updateFeature(id: number, data: any): Promise<any> {
    return this.fetch(`/features/${id}`, {
      method: 'PUT',
      data,
    }, false);
  }

  async deleteFeature(id: number): Promise<void> {
    return this.fetch(`/features/${id}`, {
      method: 'DELETE',
    }, false);
  }

  async reorderFeatures(featureIds: number[]): Promise<void> {
    return this.fetch('/features/reorder', {
      method: 'PUT',
      data: { featureIds },
    }, false);
  }

  // File upload
  async uploadFile(file: File, folder?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) {
      formData.append('folder', folder);
    }

    const response = await this.api.post('/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  // SMS Notification for reservations
  async sendReservationSMS(reservationId: string, type: 'confirm' | 'cancel', message?: string): Promise<void> {
    return this.fetch('/reserveTable/sms', {
      method: 'POST',
      data: {
        reservationId,
        type,
        message,
      },
    }, false);
  }

  // ==================== File Upload (Enhanced) ====================
  async uploadImage(file: File, folder?: string): Promise<any> {
    const formData = new FormData();
    formData.append('img', file);
    if (folder) {
      formData.append('folderName', folder);
    }

    const response = await this.api.post('/globalSetting/uploadImage', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async uploadImageWithThumbnail(file: File, folder?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) {
      formData.append('folder', folder);
    }

    const response = await this.api.post('/upload/image-with-thumbnail', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async deleteFile(fileUrl: string): Promise<void> {
    return this.fetch('/upload', {
      method: 'DELETE',
      params: { url: fileUrl },
    }, false);
  }

  // System info
  async getSystemInfo(): Promise<any> {
    return this.fetch('/admin/system');
  }

  // Cache management
  clearCache(endpoint?: string): void {
    if (endpoint) {
      this.clearRelatedCache(endpoint);
    } else {
      this.cache.clear();
    }
  }
}

// Create and export singleton instance
const adminApiService = new AdminApiService();
export default adminApiService;
